import React from "react";
import JSZip from "jszip";
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TableSortLabel } from "@mui/material";
import { enqueueSnackbar, closeSnackbar } from "notistack";
import requests from "../Auth/Requests";
import globals from '../Globals'
import DatabasePage from "./DatabasePage";
import { Page } from "../Layout/PageLayout";

export default class AssetsPage extends React.Component {
    assetInfoLoadingToken = 0;
    state = {
        showAssetDialog: false,
        assetFile: null,
        versionList: [],
        selectedBranch: null,
        clientVersions: [],
        selectedClientVersion: null,
        selectedPlatform: globals.platformNames[0],
        assetsVersions: [],
        selectedAssetsVersion: null,
        orderBy: "asset",
        order: "asc",
        assetInfo: [], // Holds fetched asset info for current manifest version
        manifestPreview: null, // { added, removed, updated }
        showUploadResultDialog: false,
        uploadResultData: null,
        pendingAssetsVersion: null, // Used to track manifestVersion after upload
    };

    componentDidMount() {
        // Parse hash for initial selector values
        const hashSelectors = this.getSelectorsFromHash();
        this.performRequest("Load version list", "list")
            .then(versionList => {
                if (versionList && versionList.length > 0) {
                    // Find the branch index from hash, or use default
                    let backendBranchIndex;
                    let selectedBranch = null;
                    if (hashSelectors.branch && globals.branches.includes(hashSelectors.branch)) {
                        selectedBranch = hashSelectors.branch;
                        backendBranchIndex = versionList.find(b => b.branch === globals.branches.indexOf(selectedBranch))?.branch;
                        if (backendBranchIndex === undefined) {
                            backendBranchIndex = versionList[0].branch;
                            selectedBranch = globals.branches.find(branch => versionList.find(v => v.branch === globals.branches.indexOf(branch))?.branch === backendBranchIndex) || null;
                        }
                    } else {
                        backendBranchIndex = versionList[0].branch;
                        selectedBranch = globals.branches.find(branch => versionList.find(v => v.branch === globals.branches.indexOf(branch))?.branch === backendBranchIndex) || null;
                    }
                    const clientVersions = this.extractClientVersions(versionList, backendBranchIndex);
                    // Find client version from hash, or use default
                    let selectedClientVersion = null;
                    if (hashSelectors.clientVersion && clientVersions.some(cv => String(cv.value) === String(hashSelectors.clientVersion))) {
                        selectedClientVersion = hashSelectors.clientVersion;
                    } else {
                        selectedClientVersion = clientVersions.length > 0 ? clientVersions[0].value : null;
                    }
                    // Find platform from hash, or use default
                    let selectedPlatform = globals.platformNames[0];
                    if (hashSelectors.platform && globals.platformNames.includes(hashSelectors.platform)) {
                        selectedPlatform = hashSelectors.platform;
                    }
                    this.setState({
                        versionList,
                        selectedBranch,
                        clientVersions,
                        selectedClientVersion,
                        selectedPlatform,
                    }, () => {
                        this.loadAssetsVersions();
                    });
                } else {
                    this.setState({
                        versionList: [],
                        selectedBranch: null,
                        clientVersions: [],
                        selectedClientVersion: null,
                    });
                }
            })
            .catch(() => {
                this.setState({
                    versionList: [],
                    selectedBranch: null,
                    clientVersions: [],
                    selectedClientVersion: null,
                });
            });
    }

    extractClientVersions(versionList, backendBranchIndex) {
        if (!versionList || backendBranchIndex === null || backendBranchIndex === undefined) return [];
        const branchData = versionList.find(b => b.branch === backendBranchIndex);
        if (!branchData || !branchData.versions) return [];
        const clientVersionsSet = new Set();
        branchData.versions.forEach(version => {
            const { major, minor } = globals.decompactVersion(version.version);
            if (version.client && Array.isArray(version.client)) {
                version.client.forEach(clientEntry => {
                    const label = `${major}.${minor}.${clientEntry.patch}`;
                    const value = globals.compactVersion(major, minor, clientEntry.patch, backendBranchIndex);
                    clientVersionsSet.add(JSON.stringify({ label, value }));
                });
            }
        });
        const clientVersionsArray = Array.from(clientVersionsSet).map(item => JSON.parse(item));
        clientVersionsArray.sort((a, b) => {
            const aParts = a.label.split('.').map(Number);
            const bParts = b.label.split('.').map(Number);
            for (let i = 0; i < 3; i++) {
                if (aParts[i] > bParts[i]) return -1;
                if (aParts[i] < bParts[i]) return 1;
            }
            return 0;
        });
        return clientVersionsArray;
    }

    performRequest(description, ...args) {
        let payload = {};
        if (typeof args[0] === "object" && args[0] !== null) {
            payload = args[0];
            args = args.slice(1);
        }
        const cmd = args.join(" ");
        const key = enqueueSnackbar(`${description}...`, { variant: 'info', persist: true });

        return requests.request("versioning " + cmd, payload)
            .then(response => {
                closeSnackbar(key);
                enqueueSnackbar(`${description} successful!`, { variant: 'success' });
                return response;
            })
            .catch(error => {
                closeSnackbar(key);
                enqueueSnackbar(`${description} failed: ${error.message}`, { variant: 'error' });
            });
    }

    async loadAssetsVersions() {
        const { selectedBranch, selectedClientVersion, selectedPlatform, versionList, pendingAssetsVersion } = this.state;
        if (!selectedBranch || !selectedClientVersion || !selectedPlatform) {
            this.setState({ assetsVersions: [], selectedAssetsVersion: null, assetInfo: [] });
            this.updateHashWithSelectors();
            return;
        }
        try {
            const response = await this.performRequest("Get assets versions", "get-assets", selectedClientVersion, selectedPlatform);
            if (Array.isArray(response)) {
                // Find the assetsVersion from hash, or use default (first in list)
                const assetsVersions = response;
                let selectedAssetsVersion = null;
                let clearPending = false;
                // If we have a pendingAssetsVersion, use it if it matches an entry
                if (pendingAssetsVersion !== null && assetsVersions.some(av => av.manifestVersion === pendingAssetsVersion)) {
                    selectedAssetsVersion = pendingAssetsVersion;
                    clearPending = true;
                } else {
                    const hashSelectors = this.getSelectorsFromHash();
                    const hashAssetsVersion = hashSelectors.assetsVersion !== undefined && hashSelectors.assetsVersion !== "" ? Number(hashSelectors.assetsVersion) : undefined;
                    if (hashAssetsVersion !== undefined && assetsVersions.some(av => av.manifestVersion === hashAssetsVersion)) {
                        selectedAssetsVersion = hashAssetsVersion;
                    } else {
                        selectedAssetsVersion = assetsVersions.length > 0 ? assetsVersions[0].manifestVersion : null;
                    }
                }
                this.setState({
                    assetsVersions,
                    selectedAssetsVersion,
                    assetInfo: [],
                    ...(clearPending ? { pendingAssetsVersion: null } : {}),
                }, async () => {
                    this.updateHashWithSelectors();
                    // Always trigger asset info loading for the manifest matching selectedAssetsVersion,
                    // even if nothing else changed (for hash navigation or initial load)
                    await this.loadAssetInfoForSelectedAssetsVersion();
                });
            } else {
                this.setState({
                    assetsVersions: [],
                    selectedAssetsVersion: null,
                    assetInfo: [],
                }, () => this.updateHashWithSelectors());
            }
        } catch (e) {
            this.setState({
                assetsVersions: [],
                selectedAssetsVersion: null,
                assetInfo: [],
            }, () => this.updateHashWithSelectors());
        }
    }

    // Always reload asset info for the manifest matching selectedAssetsVersion.
    async loadAssetInfoForSelectedAssetsVersion() {
        const { assetsVersions, selectedAssetsVersion, versionList, selectedPlatform } = this.state;
        const manifest = assetsVersions.find(av => av.manifestVersion === selectedAssetsVersion);
        if (!manifest || !manifest.assets) {
            this.setState({ assetInfo: [] });
            return;
        }
        // Determine branch enum string
        let branchEnumString = null;
        if (manifest.branch !== undefined) {
            branchEnumString = globals.branches[manifest.branch];
        } else if (versionList && versionList.length > 0) {
            branchEnumString = globals.branches[versionList[0].branch];
        }
        if (!branchEnumString) {
            this.setState({ assetInfo: [] });
            return;
        }
        const assetPairs = Object.entries(manifest.assets).map(([name, version]) => [name, version]);
        if (assetPairs.length === 0) {
            this.setState({ assetInfo: [] });
            return;
        }
        // Use the same loading token/abort logic as the selector
        this.assetInfoLoadingToken++;
        const token = this.assetInfoLoadingToken;
        const batchSize = 50;
        let results = [];
        for (let i = 0; i < assetPairs.length; i += batchSize) {
            if (this.assetInfoLoadingToken !== token) {
                // Abort further requests if token changed
                return;
            }
            const batch = assetPairs.slice(i, i + batchSize);
            const flatBatch = [];
            for (const [name, version] of batch) {
                flatBatch.push(name, version);
            }
            // Use selectedPlatform from state, always: "get-asset-info", platform, branch, [name1, version1, ...]
            // eslint-disable-next-line no-await-in-loop
            const resp = await this.performRequest("Fetch asset info", "get-asset-info", selectedPlatform, branchEnumString, ...flatBatch);
            if (this.assetInfoLoadingToken !== token) {
                // Abort after batch if token changed
                return;
            }
            if (Array.isArray(resp)) {
                results = results.concat(resp);
                this.setState({ assetInfo: [...results] });
            }
        }
    }

    /**
     * Fetch asset info for a batch of [name, version] pairs.
     * Splits into batches of 50, accumulates results.
     * @param {Array<[string, string|number]>} assetPairs
     * @param {string|number} branch
     * @returns {Promise<Array>} array of asset info objects
     */
    async fetchAssetInfoBatch(assetPairs, branch) {
        const { selectedPlatform } = this.state;
        const batchSize = 50;
        let results = [];
        for (let i = 0; i < assetPairs.length; i += batchSize) {
            const batch = assetPairs.slice(i, i + batchSize);
            // flatten to [name1, version1, name2, version2, ...]
            const flatBatch = [];
            for (const [name, version] of batch) {
                flatBatch.push(name, version);
            }
            // Always: "get-asset-info", platform, branch, [name1, version1, ...]
            // eslint-disable-next-line no-await-in-loop
            const resp = await this.performRequest("Fetch asset info", "get-asset-info", selectedPlatform, branch, ...flatBatch);
            // Assume resp is array of asset info objects
            if (Array.isArray(resp)) {
                results = results.concat(resp);
            }
        }
        return results;
    }

    handleSort(column) {
        const isAsc = this.state.orderBy === column && this.state.order === "asc";
        this.setState({
            orderBy: column,
            order: isAsc ? "desc" : "asc",
        });
    }

    getSortedAssets() {
        const { assetsVersions, orderBy, order, selectedAssetsVersion, assetInfo } = this.state;
        // Find the manifest for the selected assets version
        const manifest = assetsVersions.find(av => av.manifestVersion === selectedAssetsVersion);
        if (!manifest || !manifest.assets) return [];
        // Prepare a lookup for assetInfo by name+version
        let infoMap = null;
        if (Array.isArray(assetInfo) && assetInfo.length > 0) {
            infoMap = new Map();
            assetInfo.forEach(info => {
                // Key by name and version (both as string)
                infoMap.set(`${info.name}::${info.version}`, info);
            });
        }
        // Map manifest assets to rows, enriching with info if available
        let rows = Object.entries(manifest.assets).map(([asset, version]) => {
            let info = null;
            if (infoMap) {
                info = infoMap.get(`${asset}::${version}`) || null;
            }
            // Fill out size, modifiedVersion, date if info present
            let size = "", modifiedVersion = "", date = "";
            if (info) {
                size = info.size != null ? AssetsPage.formatSize(info.size) : "";
                if (info && info.createdVersion != null) {
                    const v = globals.decompactVersion(info.createdVersion);
                    if (v && typeof v.major === "number" && typeof v.minor === "number" && typeof v.patch === "number") {
                        modifiedVersion = `${v.major}.${v.minor}.${v.patch}`;
                    } else {
                        modifiedVersion = "";
                    }
                } else {
                    modifiedVersion = "";
                }
                date = info.createdDate != null
                    ? (() => {
                        try {
                            // Accept both Date and string
                            const d = new Date(info.createdDate);
                            return isNaN(d.getTime()) ? "" : d.toLocaleString();
                        } catch (e) { return ""; }
                    })()
                    : "";
            }
            return [asset, version, info, size, modifiedVersion, date];
        });
        // Sorting
        const sorted = rows.sort((a, b) => {
            let aValue, bValue;
            if (orderBy === "asset") {
                aValue = a[0];
                bValue = b[0];
            } else if (orderBy === "version") {
                aValue = a[1];
                bValue = b[1];
            } else {
                aValue = a[0];
                bValue = b[0];
            }
            if (aValue < bValue) return order === "asc" ? -1 : 1;
            if (aValue > bValue) return order === "asc" ? 1 : -1;
            return 0;
        });
        return sorted;
    }

    static formatSize(bytes) {
        if (typeof bytes !== "number" || isNaN(bytes)) return "";
        if (bytes < 1024) return `${bytes} B`;
        const kb = bytes / 1024;
        if (kb < 1024) return `${kb.toFixed(1)} KB`;
        const mb = kb / 1024;
        return `${mb.toFixed(1)} MB`;
    }

    render() {
        const { showAssetDialog, assetFile, versionList, selectedBranch, clientVersions, selectedClientVersion, selectedPlatform, assetsVersions, selectedAssetsVersion, orderBy, order, assetInfo, manifestPreview } = this.state;

        // Determine if Release Assets button should be enabled
        const currentManifest = assetsVersions.find(av => av.manifestVersion === selectedAssetsVersion);
        let releaseAssetsButtonEnabled;
        if (!assetsVersions || assetsVersions.length === 0) {
            // If there are no asset versions, allow releasing assets
            releaseAssetsButtonEnabled = true;
        } else {
            releaseAssetsButtonEnabled =
                selectedAssetsVersion === assetsVersions[0]?.manifestVersion &&
                currentManifest &&
                assetInfo &&
                Object.keys(currentManifest?.assets || {}).every(asset =>
                    assetInfo.some(info => info.name === asset)
                );
        }

        // Extract and format manifest creation date
        let manifestCreatedDateStr = "";
        if (currentManifest && currentManifest.createdDate) {
            try {
                const d = new Date(currentManifest.createdDate);
                if (!isNaN(d.getTime())) {
                    manifestCreatedDateStr = d.toLocaleString();
                }
            } catch (e) {
                // ignore
            }
        }

        // Upload button in dialog enabled if: file is selected, manifestPreview exists, and at least one of added/removed/updated is non-empty
        const manifestPreviewHasChanges =
            manifestPreview &&
            (
                (manifestPreview.added && manifestPreview.added.length > 0) ||
                (manifestPreview.removed && manifestPreview.removed.length > 0) ||
                (manifestPreview.updated && manifestPreview.updated.length > 0)
            );
        const uploadButtonEnabled = !!assetFile && !!manifestPreview && manifestPreviewHasChanges && !!selectedBranch && !!selectedClientVersion && !!selectedPlatform;

        return (
            <Box sx={{ padding: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', mb: 2 }}>
                    <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControl sx={{ minWidth: 120 }}>
                                <InputLabel>Branch</InputLabel>
                                <Select
                                    value={selectedBranch || ''}
                                    label="Branch"
                                    onChange={(e) => {
                                        this.assetInfoLoadingToken++;
                                        const branch = e.target.value;
                                        const branchIndex = globals.branches.indexOf(branch);
                                        const backendBranchIndex = versionList.find(b => b.branch === branchIndex)?.branch;
                                        const clientVersions = this.extractClientVersions(versionList, backendBranchIndex);
                                        const selectedClientVersion = clientVersions.length > 0 ? clientVersions[0].value : null;
                                        this.setState({
                                            selectedBranch: branch,
                                            clientVersions,
                                            selectedClientVersion,
                                            assetInfo: [],
                                        }, () => {
                                            this.updateHashWithSelectors();
                                            this.loadAssetsVersions();
                                        });
                                    }}
                                >
                                    {globals.branches.map((branch, idx) => (
                                        <MenuItem key={idx} value={branch}>{branch}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl sx={{ minWidth: 140 }}>
                                <InputLabel>Client Version</InputLabel>
                                <Select
                                    value={selectedClientVersion || ''}
                                    label="Client Version"
                                    onChange={(e) => {
                                        this.assetInfoLoadingToken++;
                                        this.setState({ selectedClientVersion: e.target.value, assetInfo: [] }, () => {
                                            this.updateHashWithSelectors();
                                            this.loadAssetsVersions();
                                        });
                                    }}
                                >
                                    {clientVersions.map(cv => (
                                        <MenuItem key={cv.value} value={cv.value}>{cv.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl sx={{ minWidth: 120 }}>
                                <InputLabel>Platform</InputLabel>
                                <Select
                                    value={selectedPlatform}
                                    label="Platform"
                                    onChange={(e) => {
                                        this.assetInfoLoadingToken++;
                                        this.setState({ selectedPlatform: e.target.value, assetInfo: [] }, () => {
                                            this.updateHashWithSelectors();
                                            this.loadAssetsVersions();
                                        });
                                    }}
                                >
                                    {globals.platformNames.map(name => (
                                        <MenuItem key={name} value={name}>{name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl sx={{ minWidth: 140 }} disabled={assetsVersions.length === 0}>
                                <InputLabel>Assets Version</InputLabel>
                                <Select
                                    value={selectedAssetsVersion || ''}
                                    label="Assets Version"
                                    onChange={async (e) => {
                                        this.assetInfoLoadingToken++;
                                        const newVersion = e.target.value;
                                        this.setState({ selectedAssetsVersion: newVersion, assetInfo: [] }, async () => {
                                            this.updateHashWithSelectors();
                                            // Always trigger asset info loading for the manifest matching selectedAssetsVersion
                                            await this.loadAssetInfoForSelectedAssetsVersion();
                                        });
                                    }}
                                >
                                    {assetsVersions.map(av => (
                                        <MenuItem key={av.manifestVersion} value={av.manifestVersion}>{av.manifestVersion}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        {/* Manifest loading info and date */}
                        {currentManifest && (() => {
                            // Only consider assets in the manifest
                            const manifestAssetNames = Object.keys(currentManifest.assets || {});
                            // Create a map of assetInfo by name for quick lookup
                            const assetInfoMap = new Map();
                            if (Array.isArray(assetInfo)) {
                                for (const info of assetInfo) {
                                    if (info && typeof info.name === "string") {
                                        assetInfoMap.set(info.name, info);
                                    }
                                }
                            }
                            // For manifest assets, check which have loaded info with a valid size (number)
                            let loadedCount = 0;
                            let totalCount = manifestAssetNames.length;
                            let allHaveValidSize = true;
                            let sizeSum = 0;
                            for (const assetName of manifestAssetNames) {
                                const info = assetInfoMap.get(assetName);
                                if (info && typeof info.size === "number" && !isNaN(info.size)) {
                                    loadedCount++;
                                    sizeSum += info.size;
                                } else {
                                    allHaveValidSize = false;
                                }
                            }
                            let percent = 0;
                            if (totalCount > 0) {
                                percent = Math.floor((loadedCount / totalCount) * 100);
                            }
                            return (
                                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', textAlign: 'left', ml: 0 }}>
                                    Created date: {manifestCreatedDateStr}
                                    {totalCount > 0 && allHaveValidSize
                                        ? <> — Total size: {AssetsPage.formatSize(sizeSum)}</>
                                        : (totalCount > 0 && loadedCount < totalCount
                                            ? <> — Loading asset infos: {percent}%</>
                                            : null
                                        )
                                    }
                                </Typography>
                            );
                        })()}
                    </Box>
                    <Button
                        variant="outlined"
                        sx={{ alignSelf: 'flex-end', ml: 2 }}
                        onClick={() => this.setState({ showAssetDialog: true })}
                        disabled={!releaseAssetsButtonEnabled}
                    >
                        Release Assets
                    </Button>
                </Box>
                <Dialog
                    open={showAssetDialog}
                    onClose={() => this.setState({ showAssetDialog: false, assetFile: null, manifestPreview: null })}
                    maxWidth={false}
                    PaperProps={{ sx: { width: 900, height: 700 } }}
                >
                    <DialogTitle>Release Assets</DialogTitle>
                    <DialogContent>
                        <Button
                            variant="outlined"
                            component="label"
                            fullWidth
                            sx={{ mt: 2 }}
                        >
                            Upload Zip File
                            <input
                                type="file"
                                accept=".zip"
                                hidden
                                onChange={async (e) => {
                                    const file = e.target.files[0];
                                    this.setState({ assetFile: file, manifestPreview: null });
                                    if (!file) {
                                        this.setState({ manifestPreview: null });
                                        return;
                                    }
                                    try {
                                        const zip = await JSZip.loadAsync(file);
                                        const manifestFile = zip.file("manifest.json");
                                        if (!manifestFile) {
                                            this.setState({ manifestPreview: null });
                                            enqueueSnackbar("manifest.json not found in zip", { variant: "error" });
                                            return;
                                        }
                                        const manifestText = await manifestFile.async("string");
                                        let uploadedManifest;
                                        try {
                                            uploadedManifest = JSON.parse(manifestText);
                                        } catch (err) {
                                            this.setState({ manifestPreview: null });
                                            enqueueSnackbar("Invalid manifest.json", { variant: "error" });
                                            return;
                                        }
                                        // uploadedManifest: { files: { [file]: { hash, size } } }
                                        const uploadedAssets = uploadedManifest.files || {};
                                        // assetInfo: array of { name, version, hash, ... }
                                        // Build currentHashes: { [fileName]: hash }
                                        const currentHashes = {};
                                        for (const info of (assetInfo || [])) {
                                            if (info && typeof info.name === "string" && typeof info.hash === "string") {
                                                currentHashes[info.name] = info.hash;
                                            }
                                        }
                                        let added = [];
                                        let removed = [];
                                        let updated = [];
                                        // If there is no currentManifest or no assetInfo, treat all files as added
                                        if (!currentManifest || !assetInfo || assetInfo.length === 0) {
                                            added = Object.keys(uploadedAssets);
                                            removed = [];
                                            updated = [];
                                        } else {
                                            // Compare keys and hashes
                                            for (const fileName of Object.keys(uploadedAssets)) {
                                                const uploadedHash = uploadedAssets[fileName]?.hash;
                                                const currentHash = currentHashes[fileName];
                                                if (!(fileName in currentHashes)) {
                                                    added.push(fileName);
                                                } else if (uploadedHash !== currentHash) {
                                                    updated.push(fileName);
                                                }
                                            }
                                            // For removed, use keys from currentHashes (not manifest assets)
                                            for (const fileName of Object.keys(currentHashes)) {
                                                if (!(fileName in uploadedAssets)) {
                                                    removed.push(fileName);
                                                }
                                            }
                                        }
                                        this.setState({
                                            manifestPreview: { added, removed, updated, uploadedAssets }
                                        });
                                    } catch (err) {
                                        this.setState({ manifestPreview: null });
                                        enqueueSnackbar("Failed to read zip file", { variant: "error" });
                                    }
                                }}
                            />
                        </Button>
                        {assetFile && (
                            <>
                                <Typography variant="body2" sx={{ mt: 1 }}>{assetFile.name}</Typography>
                                {/* Show total size if manifestPreview.uploadedAssets has files with valid size */}
                                {(() => {
                                    if (
                                        manifestPreview &&
                                        manifestPreview.uploadedAssets &&
                                        typeof manifestPreview.uploadedAssets === "object"
                                    ) {
                                        // Get all sizes from uploadedAssets (only those with a valid number)
                                        const sizes = Object.values(manifestPreview.uploadedAssets)
                                            .map(f => f && typeof f.size === "number" && !isNaN(f.size) ? f.size : null)
                                            .filter(sz => sz !== null);
                                        if (sizes.length > 0) {
                                            const totalSize = sizes.reduce((a, b) => a + b, 0);
                                            return (
                                                <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                                                    Total size: {AssetsPage.formatSize(totalSize)}
                                                </Typography>
                                            );
                                        }
                                    }
                                    return null;
                                })()}
                            </>
                        )}
                        {/* Manifest preview */}
                        {manifestPreview && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2">Manifest Preview:</Typography>
                                <Box sx={{ height: 400, overflowY: "auto", mt: 1, pr: 1 }}>
                                    {manifestPreview.added && manifestPreview.added.length > 0 && (
                                        <Box sx={{ ml: 1 }}>
                                            <Typography variant="body2" color="success.main">Added files:</Typography>
                                            <ul style={{ marginTop: 0 }}>
                                                {manifestPreview.added.map(f => (
                                                    <li key={"added-" + f}>
                                                        {f}
                                                        {manifestPreview.uploadedAssets && manifestPreview.uploadedAssets[f] && typeof manifestPreview.uploadedAssets[f].size === "number"
                                                            ? ` (${AssetsPage.formatSize(manifestPreview.uploadedAssets[f].size)})`
                                                            : ""}
                                                    </li>
                                                ))}
                                            </ul>
                                        </Box>
                                    )}
                                    {manifestPreview.removed && manifestPreview.removed.length > 0 && (
                                        <Box sx={{ ml: 1 }}>
                                            <Typography variant="body2" color="error.main">Removed files:</Typography>
                                            <ul style={{ marginTop: 0 }}>
                                                {manifestPreview.removed.map(f => <li key={"removed-" + f}>{f}</li>)}
                                            </ul>
                                        </Box>
                                    )}
                                    {manifestPreview.updated && manifestPreview.updated.length > 0 && (
                                        <Box sx={{ ml: 1 }}>
                                            <Typography variant="body2" color="warning.main">Updated files:</Typography>
                                            <ul style={{ marginTop: 0 }}>
                                                {manifestPreview.updated.map(f => (
                                                    <li key={"updated-" + f}>
                                                        {f}
                                                        {manifestPreview.uploadedAssets && manifestPreview.uploadedAssets[f] && typeof manifestPreview.uploadedAssets[f].size === "number"
                                                            ? ` (${AssetsPage.formatSize(manifestPreview.uploadedAssets[f].size)})`
                                                            : ""}
                                                    </li>
                                                ))}
                                            </ul>
                                        </Box>
                                    )}
                                    {manifestPreview.added.length === 0 && manifestPreview.removed.length === 0 && manifestPreview.updated.length === 0 && (
                                        <Typography variant="body2" sx={{ ml: 1 }}>No changes detected.</Typography>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => this.setState({ showAssetDialog: false, assetFile: null, manifestPreview: null })}>Cancel</Button>
                        <Button
                            variant="contained"
                            disabled={!uploadButtonEnabled}
                            onClick={async () => {
                                if (!assetFile || !selectedBranch || !selectedClientVersion || !selectedPlatform) return;
                                if (!manifestPreviewHasChanges) return;
                                this.performRequest("Upload assets", assetFile,
                                    "upload-assets", selectedClientVersion, selectedPlatform
                                ).then(result => {
                                    // Do NOT parse result as JSON; assume already parsed object
                                    let uploadResult = result;
                                    // Capture manifestVersion as pendingAssetsVersion if present
                                    this.setState({
                                        showAssetDialog: false,
                                        assetFile: null,
                                        manifestPreview: null,
                                        showUploadResultDialog: !!uploadResult,
                                        uploadResultData: uploadResult,
                                        pendingAssetsVersion: (result && result.manifestVersion) ? result.manifestVersion : null,
                                    }, () => { this.loadAssetsVersions(); });
                                })
                                    .catch(err => {
                                        // Optionally notify user of error
                                    });
                            }}
                        >
                            Upload
                        </Button>
                    </DialogActions>
                </Dialog>
                {/* Upload result dialog */}
                <Dialog
                    open={this.state.showUploadResultDialog}
                    onClose={() => this.setState({ showUploadResultDialog: false })}
                    maxWidth="md"
                    PaperProps={{ sx: { width: 600, maxHeight: 500 } }}
                >
                    <DialogTitle>Upload Result</DialogTitle>
                    <DialogContent dividers sx={{ maxHeight: 350, overflowY: "auto" }}>
                        {(() => {
                            const data = this.state.uploadResultData;
                            if (!data) return <Typography>No result data.</Typography>;
                            // Prepare updated list: iterate over object keys, only include changed === true
                            let updated = [];
                            if (data.updated && typeof data.updated === "object" && !Array.isArray(data.updated)) {
                                updated = Object.keys(data.updated)
                                    .filter(fileName => data.updated[fileName] && data.updated[fileName].changed === true)
                                    .map(fileName => ({
                                        name: fileName,
                                        newSize: data.updated[fileName].newSize,
                                        oldSize: data.updated[fileName].oldSize,
                                    }));
                            }
                            // Separate into added and updated
                            const added = updated.filter(f => f.oldSize === 0);
                            const updatedOnly = updated.filter(f => f.oldSize !== 0);
                            // Show all files in removed array
                            const removed = Array.isArray(data.removed) ? data.removed : [];
                            // Only show each block if there are items
                            return (
                                <Box>
                                    {removed.length > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" color="error.main">Removed files:</Typography>
                                            <ul style={{ marginTop: 0 }}>
                                                {removed.map((f, idx) => (
                                                    <li key={"removed-" + idx}>
                                                        {typeof f === "string" ? f : (f.name || JSON.stringify(f))}
                                                    </li>
                                                ))}
                                            </ul>
                                        </Box>
                                    )}
                                    {added.length > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" color="success.main">Added files:</Typography>
                                            <ul style={{ marginTop: 0 }}>
                                                {added.map((f, idx) => (
                                                    <li key={"added-" + idx}>
                                                        {f.name}
                                                        {typeof f.newSize === "number"
                                                            ? ` (${AssetsPage.formatSize(f.newSize)})`
                                                            : ""}
                                                    </li>
                                                ))}
                                            </ul>
                                        </Box>
                                    )}
                                    {updatedOnly.length > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" color="warning.main">Updated files:</Typography>
                                            <ul style={{ marginTop: 0 }}>
                                                {updatedOnly.map((f, idx) => (
                                                    <li key={"updated-" + idx}>
                                                        {f.name}
                                                        {typeof f.newSize === "number"
                                                            ? ` (${AssetsPage.formatSize(f.newSize)})`
                                                            : ""}
                                                    </li>
                                                ))}
                                            </ul>
                                        </Box>
                                    )}
                                    {removed.length === 0 && added.length === 0 && updatedOnly.length === 0 && (
                                        <Typography>No files were added, updated, or removed.</Typography>
                                    )}
                                </Box>
                            );
                        })()}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => this.setState({ showUploadResultDialog: false })}>OK</Button>
                    </DialogActions>
                </Dialog>
                <TableContainer component={Paper}>
                    <Table>
                <TableHead>
                    <TableRow>
                        <TableCell sortDirection={orderBy === "asset" ? order : false}>
                            <TableSortLabel
                                active={orderBy === "asset"}
                                direction={orderBy === "asset" ? order : "asc"}
                                onClick={() => this.handleSort("asset")}
                            >
                                Asset
                            </TableSortLabel>
                        </TableCell>
                        <TableCell sortDirection={orderBy === "version" ? order : false}>
                            <TableSortLabel
                                active={orderBy === "version"}
                                direction={orderBy === "version" ? order : "asc"}
                                onClick={() => this.handleSort("version")}
                            >
                                Version
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>Size</TableCell>
                        <TableCell>Modified Version</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Inspect</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {this.getSortedAssets().map(([asset, version, info, size, modifiedVersion, date]) => (
                        <TableRow key={asset}>
                            <TableCell>{asset}</TableCell>
                            <TableCell>{version}</TableCell>
                            <TableCell>{size}</TableCell>
                            <TableCell>{modifiedVersion}</TableCell>
                            <TableCell>{date}</TableCell>
                            <TableCell>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => {
                                        // Find current manifest to get branch index
                                        const currentManifest = this.state.assetsVersions.find(av => av.manifestVersion === this.state.selectedAssetsVersion);
                                        let branchEnumString = "";
                                        if (currentManifest && typeof currentManifest.branch !== "undefined") {
                                            branchEnumString = globals.branches[currentManifest.branch];
                                        }
                                        // Fallback: use selectedBranch if not found
                                        if (!branchEnumString && this.state.selectedBranch) {
                                            branchEnumString = this.state.selectedBranch;
                                        }
                                        const platform = this.state.selectedPlatform;
                                        // Compose hash: #<id>-<sub>-<branch>-<platform>-<asset>
                                        window.location.hash = `#${Page.Database.id}-${Page.Database.subs.AssetInspector.id}-${branchEnumString}-${platform}-${encodeURIComponent(asset)}`;
                                    }}
                                >
                                    Inspect
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    }

    // Helper: update the URL hash based on current selector state
    updateHashWithSelectors() {
        const { selectedBranch, selectedClientVersion, selectedPlatform, selectedAssetsVersion } = this.state;
        // Use empty string for any missing value
        const branch = selectedBranch || "";
        const clientVersion = selectedClientVersion || "";
        const platform = selectedPlatform || "";
        const assetsVersion = selectedAssetsVersion || "";
        // Keep the first two segments of the hash (e.g., "#3-0"), replace the rest
        const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
        const hashParts = hash.split('-');
        let prefix = "";
        if (hashParts.length >= 2 && hashParts[0] !== "" && hashParts[1] !== "") {
            prefix = `#${hashParts[0]}-${hashParts[1]}`;
        } else {
            // Fallback: if no prefix, just keep hash as empty
            prefix = "";
        }
        const selectorString = `${branch}-${clientVersion}-${platform}-${assetsVersion}`;
        let newHash;
        if (prefix) {
            newHash = `${prefix}-${selectorString}`;
        } else {
            newHash = `#${selectorString}`;
        }
        // Use history.replaceState to update hash without adding a new history entry
        window.history.replaceState(null, "", newHash);
    }

    // Helper: parse the URL hash and return {branch, clientVersion, platform, assetsVersion}
    getSelectorsFromHash() {
        const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
        // Find first two segments (page and subpage), skip them, parse selectors from the rest
        const hashParts = hash.split('-');
        let selectorParts;
        if (hashParts.length >= 4) {
            // Skip first two
            selectorParts = hashParts.slice(2);
        } else {
            selectorParts = hashParts;
        }
        const [branch, clientVersion, platform, assetsVersion] = selectorParts;
        return {
            branch: branch || undefined,
            clientVersion: clientVersion || undefined,
            platform: platform || undefined,
            assetsVersion: assetsVersion || undefined,
        };
    }
}